import { Box, useEventCallback } from "@mui/material";
import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import {
  WasteScheduleAdherenceIssueCode,
  WasteScheduleAdherenceQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2, useOmnisearchStore } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { FilterKey } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AuditErrorBoundary } from "routes/Customer/Waste/Invoices/Audit/AuditErrorBoundary";
import { formatIssueLabel } from "routes/Customer/Waste/Invoices/Audit/formatAuditValues";
import { INVOICE_TRACKING_HEADER_HEIGHT } from "routes/Customer/Waste/Invoices/InvoiceTrackingDateFilter";
import {
  periodError,
  useClearFiltersToPeriod,
  useInvoiceTrackingPeriod,
  usePeriodDisplayTokens,
  withoutPeriodTerm,
} from "routes/Customer/Waste/Invoices/invoiceTrackingPeriod";
import { WasteListPageSurface } from "routes/Customer/Waste/WasteListPageSurface";

import { COLUMNS, DEFAULT_COLUMN_VISIBILITY, ExceptionRow, getCount, getItems } from "./columns";
import { WASTE_SCHEDULE_ADHERENCE_QUERY } from "./queries";
import { ResultDrawer } from "./ResultDrawer";
import { PeriodTotals, ReportIntro } from "./SummaryTiles";

const PERSISTENCE_KEY = "waste-schedule-adherence";
const INITIAL_SORT: GridSortModel = [{ field: "daysOutside", sort: "desc" }];
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const RESULT_PARAM = "result";

/** Filters with no column of their own. */
const ADDITIONAL_FILTER_KEYS: FilterKey[] = [
  {
    key: "issue",
    header: "Reason",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(WasteScheduleAdherenceIssueCode),
    enumPresentationFunction: formatIssueLabel,
    ignoreStateAbbreviations: true,
  },
  { key: "facilityId", header: "Facility", filterKeyType: "facility" },
];

const FILTER_GROUPS: FilterGroup[] = [
  { name: "Status", fields: ["status", "issue"] },
  { name: "Location", fields: ["facilityId"] },
  { name: "Service", fields: ["vendor", "kind", "serviceDate", "daysOutside"] },
];

/**
 * The open result, held in the URL so the back button closes it and the row can
 * be linked. Key from `resultKey`.
 */
function useOpenResult(rows: readonly ExceptionRow[]) {
  const [params, setParams] = useSearchParams();
  const openKey = params.get(RESULT_PARAM);
  const opened = openKey == null ? null : (rows.find((row) => row.id === openKey) ?? null);

  // Focus goes back to the row that opened the drawer once it closes.
  const triggerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (openKey != null) return;
    if (triggerRef.current?.isConnected) triggerRef.current.focus({ preventScroll: true });
    triggerRef.current = null;
  }, [openKey]);

  // An event callback rather than useCallback: the router hands out a new
  // setter on every URL change, which would otherwise re-render the whole grid
  // each time a drawer opens or closes.
  const setOpen = useEventCallback((key: string | null) => {
    if (key != null && document.activeElement instanceof HTMLElement) {
      triggerRef.current = document.activeElement;
    }
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (key == null) updated.delete(RESULT_PARAM);
        else updated.set(RESULT_PARAM, key);
        return updated;
      },
      // Opening pushes history so the back button closes the drawer; switching
      // rows or closing replaces it, so back never reopens a closed drawer.
      { replace: key == null || openKey != null, preventScrollReset: true }
    );
  });

  return { opened, setOpen };
}

export function WasteScheduleAdherence() {
  const { tenantId } = useTenant();
  const { period } = useInvoiceTrackingPeriod();
  const [rows, setRows] = useState<readonly ExceptionRow[]>([]);
  // Bumped to remount the report after a failure, so Retry re-asks rather than
  // re-rendering the error it already has.
  const [retry, setRetry] = useState(0);
  const onRetry = useCallback(() => setRetry((count) => count + 1), []);
  const clearFilters = useClearFiltersToPeriod();
  const { omnisearch } = useOmnisearchStore();
  const invalidPeriod = periodError(period);
  // The bar above the tabs owns the period; a pill for it here would be a
  // second control.
  const hiddenDisplayTokens = [...usePeriodDisplayTokens()];
  const hasPeriod = period.from !== "" && period.through !== "";
  const { opened, setOpen } = useOpenResult(rows);
  const openResult = useCallback(
    (params: { row: ExceptionRow }) => setOpen(params.row.id),
    [setOpen]
  );
  const closeResult = useCallback(() => setOpen(null), [setOpen]);

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Invoice Tracking", to: tenantId ? `/o/${tenantId}/waste/invoices` : undefined },
    { label: "Schedule Adherence" },
  ]);

  const buildVariables = useCallback(
    (
      search: string,
      pagination: GridPaginationModel,
      sort: GridSortModel
    ): WasteScheduleAdherenceQueryVariables => ({
      tenantId: tenantId ?? "",
      ...period,
      // The shared bar's term is the period this report grades, not a filter on
      // its rows, and the audit refuses a key it does not know.
      search: withoutPeriodTerm(search),
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map(({ field, sort: direction }) => ({ field, sort: direction })),
    }),
    [tenantId, period]
  );

  if (!tenantId) return null;

  return (
    <>
      <WasteListPageSurface
        headerOffset={INVOICE_TRACKING_HEADER_HEIGHT}
        above={
          hasPeriod && invalidPeriod == null ? (
            // The totals describe the period, not the rows a filter left behind,
            // so they sit on the canvas above the list rather than inside its card.
            <Box sx={{ mb: 3 }}>
              <PeriodTotals tenantId={tenantId} from={period.from} through={period.through} />
            </Box>
          ) : null
        }
      >
        {/* A period the report would only refuse is not asked for at all: the
            picker above says why, and the page below stays as it was. */}
        {hasPeriod && invalidPeriod == null ? (
          <AuditErrorBoundary
            message="Unable to load schedule adherence."
            // Includes the search so a refusal clears on any change, including
            // "Clear filters".
            resetKey={`${omnisearch}|${retry}`}
            onRetry={onRetry}
            onClearFilters={clearFilters}
          >
            {/* The card carries no horizontal padding of its own, so the intro,
                toolbar and rows inside it are inset the same. */}
            <Box sx={{ px: 3, mb: 2 }}>
              <ReportIntro tenantId={tenantId} from={period.from} through={period.through} />
            </Box>
            <Box sx={{ px: 3 }}>
              {/* A different period is a different report, so the grid starts over
                  on page one rather than on whichever page the last one was on. */}
              <OmnisearchDataGridV2
                key={`${period.from}|${period.through}|${retry}`}
                compact
                tenantId={tenantId}
                persistenceKey={PERSISTENCE_KEY}
                query={WASTE_SCHEDULE_ADHERENCE_QUERY}
                getItems={getItems}
                getCount={getCount}
                columns={COLUMNS}
                buildVariables={buildVariables}
                additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
                filterGroups={FILTER_GROUPS}
                initialSortModel={INITIAL_SORT}
                initialColumnVisibilityModel={DEFAULT_COLUMN_VISIBILITY}
                initialPageSize={25}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                rowHeight={56}
                disableRowSelectionOnClick
                onRowClick={openResult}
                hiddenDisplayTokens={hiddenDisplayTokens}
                onItemsLoaded={setRows}
                noRowsMessage="No result matches these filters."
              />
            </Box>
          </AuditErrorBoundary>
        ) : null}
        <ResultDrawer row={opened} onClose={closeResult} />
      </WasteListPageSurface>
    </>
  );
}
